import React from "react";
import { useNavigate } from "react-router-dom";
import "./styles/About.css";

const About = () => {
  const navigate = useNavigate();

  const pillars = [
    {
      title: "Networking & Infrastructure",
      text:
        "Architect and deploy reliable networks for modern workplaces built to scale, easy to manage, and resilient under pressure.",
    },
    {
      title: "Cloud Enablement",
      text:
        "Modernize infrastructure with practical cloud strategies that improve flexibility while keeping cost and security in check.",
    },
    {
      title: "Cybersecurity Services",
      text:
        "Strengthen your security posture with proactive controls, risk reduction, and incident ready practices designed for real environments.",
    },
  ];

  const values = [
    {
      title: "Customer first delivery",
      text:
        "Clear communication, clean documentation, and solutions that fit your business not one size fits all setups.",
    },
    {
      title: "Deep technical expertise",
      text:
        "Experience across enterprise technologies to design secure, scalable, and high-performing IT environments.",
    },
    {
      title: "Future proof thinking",
      text:
        "Infrastructure decisions made with growth, reliability, and long-term maintainability in mind.",
    },
  ];

  const audiences = [
    "Enterprises running mission critical environments.",
    "Public sector organizations with high reliability and security expectations.",
    "Teams managing complex, multi vendor infrastructure and cloud ecosystems.",
  ];

  const expectations = [
    "Clear scope and outcomes.",
    "Security minded architecture decisions.",
    "Documentation you can actually use.",
    "Support that scales with your needs.",
  ];

  return (
    <>
      {/* HERO */}
      <section className="aboutHero">
        <div className="aboutHero__grid-bg" aria-hidden="true"></div>

        <div className="aboutHero__container">
          <div className="aboutHero__left">
            <div className="aboutEyebrow">
              <span className="live-dot" aria-hidden="true" />
              About ALG DataGuard
            </div>

            <h1 className="aboutHero__title">
              Secure IT. Strong networks. Confident growth.
            </h1>

            <p className="aboutHero__lead">
              ALG DataGuard is an IT consulting and cybersecurity services
              company focused on networking, cloud, security, and IT
              infrastructure supporting organizations that need reliability,
              scale, and a stronger security posture.
            </p>

            {/* NEW — uses the existing .aboutHero__badge class, which was
                already defined in About.css but never used anywhere */}
            <div className="aboutHero__badge">
              🇨🇦 Canadian Owned and Operated
            </div>

            <div className="aboutHero__ctaRow">
              <button
                className="aboutBtn aboutBtn--primary"
                type="button"
                onClick={() => navigate("/contact")}
              >
                Talk to an Expert
              </button>
              <button
                className="aboutBtn aboutBtn--ghost"
                type="button"
                onClick={() => navigate("/services")}
              >
                Explore Services
              </button>
            </div>

            <div className="aboutHero__miniStats">
              <div className="aboutStat">
                <div className="aboutStat__num">Networking</div>
                <div className="aboutStat__text">Design, deployment, and optimization.</div>
              </div>
              <div className="aboutStat">
                <div className="aboutStat__num">Cloud</div>
                <div className="aboutStat__text">Modernize without disrupting operations.</div>
              </div>
              <div className="aboutStat">
                <div className="aboutStat__num">Security</div>
                <div className="aboutStat__text">Practical protection for real threats.</div>
              </div>
            </div>
          </div>

          <div className="aboutHero__right">
            <div className="aboutHero__visual">
              <span className="scan-corner tl" aria-hidden="true" />
              <span className="scan-corner tr" aria-hidden="true" />
              <span className="scan-corner bl" aria-hidden="true" />
              <span className="scan-corner br" aria-hidden="true" />

              <div className="aboutHero__visualHead">
                <span className="aboutHero__visualDot" aria-hidden="true" />
                Overview
              </div>

              <h3 className="aboutHero__cardTitle">Building Secure Digital Infrastructure</h3>
              <p className="aboutHero__cardText">
                Delivering enterprise networking, cloud, cybersecurity, AI, and IT infrastructure solutions that help organizations operate securely, scale confidently, and stay ahead of evolving technology.
              </p>

              <div className="aboutHero__tags">
                <span>Networking</span>
                <span>Cloud</span>
                <span>Security</span>
              </div>

              <div className="aboutHero__locations">
                <div className="aboutLocationCard">
                  <span className="location-dot" />
                  <div>
                    <h4>Canada</h4>
                    <p>Ontario • Quebec • Alberta • British Columbia (BC)</p>
                  </div>
                </div>
                {/* HERO 
                <div className="aboutLocationCard">
                  <span className="location-dot" />
                  <div>
                    <h4>United States</h4>
                    <p>Texas • Florida • New York • New Hampshire • Illinois • Pennsylvania</p>
                  </div>
                </div>

                <div className="aboutLocationCard">
                  <span className="location-dot" />
                  <div>
                    <h4>Global Presence</h4>
                    <p>India • Qatar • UAE • Saudi Arabia • Singapore • Malaysia • Australia</p>
                  </div>
                </div>*/}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="aboutSection aboutSection--white">
        <div className="aboutContainer">
          <div className="aboutEyebrow aboutEyebrow--dark">
            <span className="live-dot" aria-hidden="true" />
            Who we are
          </div>
          <h2 className="aboutH2">A one-stop IT consulting partner</h2>
          <p className="aboutP">
            ALG DataGuard positions itself as a "one-stop IT consulting
            partner" with a focus on secure, scalable solutions for modern
            organizations.
          </p>
          <p className="aboutP">
            The work centers on building reliable IT foundations networking,
            cloud, and security infrastructure so teams can operate with
            confidence and reduce technology related risk.
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section className="aboutSection aboutSection--muted">
        <div className="aboutContainer">
          <div className="aboutEyebrow aboutEyebrow--dark">
            <span className="live-dot" aria-hidden="true" />
            Focus areas
          </div>
          <h2 className="aboutH2">What we focus on</h2>

          <div className="aboutGrid3">
            {pillars.map((p) => (
              <article key={p.title} className="aboutCard">
                <span className="scan-corner tl" aria-hidden="true" />
                <span className="scan-corner tr" aria-hidden="true" />
                <span className="scan-corner bl" aria-hidden="true" />
                <span className="scan-corner br" aria-hidden="true" />
                <h3 className="aboutCard__title">{p.title}</h3>
                <p className="aboutCard__text">{p.text}</p>
              </article>
            ))}
          </div>

          <div className="aboutHighlight">
            <div className="aboutHighlight__bar" />
            <p className="aboutHighlight__text">
              Built for environments where uptime, performance, and security
              can't be optional.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES + AUDIENCE */}
      <section className="aboutSection aboutSection--white">
        <div className="aboutContainer">
          <div className="aboutSplit">
            <div>
              <h2 className="aboutH2">How we work</h2>
              <div className="aboutGrid2">
                {values.map((v) => (
                  <article key={v.title} className="aboutCard aboutCard--tight">
                    <h3 className="aboutCard__title">{v.title}</h3>
                    <p className="aboutCard__text">{v.text}</p>
                  </article>
                ))}
              </div>
            </div>

            <div>
              <h2 className="aboutH2">Who we help</h2>
              <p className="aboutP">
                ALG DataGuard's positioning aligns with organizations that
                need mission critical reliability and strong security
                practices.
              </p>
              <ul className="aboutList">
                {audiences.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>

              <div className="aboutNote">
                <span className="aboutNote__dot" />
                <p className="aboutNote__text">
                  Engagements can start with assessment and planning, then
                  move into deployment and ongoing improvement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="aboutCta">
        <div className="aboutCta__grid-bg" aria-hidden="true"></div>

        <div className="aboutCta__container">
          <div className="aboutCta__left">
            <div className="aboutEyebrow">
              <span className="live-dot" aria-hidden="true" />
              Ready when you are
            </div>

            <h2 className="aboutCta__title">
              Ready to strengthen your IT foundation?
            </h2>
            <p className="aboutCta__text">
              Start with a conversation. Get practical guidance on
              networking, cloud, and cybersecurity priorities without the
              noise.
            </p>

            <div className="aboutCta__buttons">
              <button
                className="aboutBtn aboutBtn--primary"
                type="button"
                onClick={() => navigate("/contact")}
              >
                Book a Consultation
              </button>
              <button
                className="aboutBtn aboutBtn--ghostOnDark"
                type="button"
                onClick={() => navigate("/services")}
              >
                View Services
              </button>
            </div>
          </div>

          <div className="aboutCta__right">
            <div className="aboutCta__panel">
              <span className="scan-corner tl" aria-hidden="true" />
              <span className="scan-corner tr" aria-hidden="true" />
              <span className="scan-corner bl" aria-hidden="true" />
              <span className="scan-corner br" aria-hidden="true" />

              <h3 className="aboutCta__panelTitle">What you can expect</h3>
              <ul className="aboutCta__panelList">
                {expectations.map((item) => (
                  <li key={item}>
                    <svg
                      className="aboutCta__check"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;